import { html, ViewTemplate, ref, when } from '@microsoft/fast-element';
import type { MyPortfolio } from './my-portfolio';
import { plusIcon } from '../../../../styles';
import { PROFILE_RIGHTS } from '../../../../utils';
import { MORE_INFO_ITEMS } from '../../../../utils/enums/more-info.enum';
import { MoreInfoIconSize } from '../../../common/more-info/more-info-icon/more-info-icon.template';

export enum BUYER_DASHBOARD_GRID_TABS {
  TRADING_ACTIVITY_TAB = 'tradingActivity',
  TRADING_ACTIVITY_PANEL = 'tradingActivityPanel',
  ACTIVE_RFQS_TAB = 'activeRFQs',
  ACTIVE_RFQS_PANEL = 'activeRFQsPanel',
  INACTIVE_RFQS_TAB = 'inactiveRFQs',
  INACTIVE_RFQS_PANEL = 'inactiveRFQsPanel',
  NEGOTIATIONS_TAB = 'negotiations',
  NEGOTIATIONS_PANEL = 'negotiationsPanel',
  EXECUTED_TAB = 'executed',
  EXECUTED_PANEL = 'executedPanel',
  TERMINATED_TAB = 'terminated',
  TERMINATED_PANEL = 'terminatedPanel',
}

export const MyPortfolioTemplate: ViewTemplate<MyPortfolio> = html`
  <div class="container">
    <zero-tabs ${ref('rfqGridTabs')} activeId=${BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB}>
      <! -- TABS -->
      ${when(
        (x) => !x.showOnlyNegotiations(),
        html<MyPortfolio>`
          <zero-tab
            slot="tab"
            id=${BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB}
            @click=${(x) => x.allRfqsTradingActivity()}
          >
            Trading Activity
            <more-info-icon
              :isVisible=${(x) =>
                x.rfqGridTabs.activeid == BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB}
              :moreInfoItem=${() => MORE_INFO_ITEMS.BUYER_TRADING_ACTIVITY}
              :size=${() => MoreInfoIconSize.SMALL}
            ></more-info-icon>
          </zero-tab>
          <zero-tab
            slot="tab"
            id=${BUYER_DASHBOARD_GRID_TABS.ACTIVE_RFQS_TAB}
            @click=${(x) => x.allActiveRfqs()}
          >
            Active RFQ's
            <more-info-icon
              :isVisible=${(x) =>
                x.rfqGridTabs.activeid == BUYER_DASHBOARD_GRID_TABS.ACTIVE_RFQS_TAB}
              :moreInfoItem=${() => MORE_INFO_ITEMS.ACTIVE_RFQS}
              :size=${() => MoreInfoIconSize.SMALL}
            ></more-info-icon>
          </zero-tab>
          <zero-tab
            slot="tab"
            id=${BUYER_DASHBOARD_GRID_TABS.INACTIVE_RFQS_TAB}
            @click=${(x) => x.allInactiveRfqs()}
          >
            Inactive RFQ's
            <more-info-icon
              :isVisible=${(x) =>
                x.rfqGridTabs.activeid == BUYER_DASHBOARD_GRID_TABS.INACTIVE_RFQS_TAB}
              :moreInfoItem=${() => MORE_INFO_ITEMS.INACTIVE_RFQS}
              :size=${() => MoreInfoIconSize.SMALL}
            ></more-info-icon>
          </zero-tab>
        `,
      )}
      <zero-tab
        id=${BUYER_DASHBOARD_GRID_TABS.NEGOTIATIONS_TAB}
        slot="tab"
        @click=${(x) => x.allNegotiations()}
      >
        Negotiations
        <more-info-icon
          :isVisible=${(x) => x.rfqGridTabs.activeid == BUYER_DASHBOARD_GRID_TABS.NEGOTIATIONS_TAB}
          :moreInfoItem=${() => MORE_INFO_ITEMS.NEGOTIATIONS}
          :size=${() => MoreInfoIconSize.SMALL}
        ></more-info-icon>
      </zero-tab>
      <zero-tab
        id=${BUYER_DASHBOARD_GRID_TABS.EXECUTED_TAB}
        slot="tab"
        @click=${(x) => x.allExecutedNegotiations()}
      >
        Executed
        <more-info-icon
          :isVisible=${(x) => x.rfqGridTabs.activeid == BUYER_DASHBOARD_GRID_TABS.EXECUTED_TAB}
          :moreInfoItem=${() => MORE_INFO_ITEMS.EXECUTED}
          :size=${() => MoreInfoIconSize.SMALL}
        ></more-info-icon>
      </zero-tab>
      ${when(
        (x) => !x.showOnlyNegotiations(),
        html`
          <zero-tab
            id=${BUYER_DASHBOARD_GRID_TABS.TERMINATED_TAB}
            slot="tab"
            @click=${(x) => x.allTerminatedNegotiations()}
          >
            Terminated
            <more-info-icon
              :isVisible=${(x) =>
                x.rfqGridTabs.activeid == BUYER_DASHBOARD_GRID_TABS.TERMINATED_TAB}
              :moreInfoItem=${() => MORE_INFO_ITEMS.TERMINATED}
              :size=${() => MoreInfoIconSize.SMALL}
            ></more-info-icon>
          </zero-tab>
        `,
      )}

      <!-- PANELS -->
      ${when(
        (x) => !x.showOnlyNegotiations(),
        html`
          <zero-tab-panel
            slot="tabpanel"
            id=${BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_PANEL}
          ></zero-tab-panel>
          <zero-tab-panel
            slot="tabpanel"
            id=${BUYER_DASHBOARD_GRID_TABS.ACTIVE_RFQS_PANEL}
          ></zero-tab-panel>
          <zero-tab-panel
            slot="tabpanel"
            id=${BUYER_DASHBOARD_GRID_TABS.INACTIVE_RFQS_PANEL}
          ></zero-tab-panel>
        `,
      )}
      <zero-tab-panel
        id=${BUYER_DASHBOARD_GRID_TABS.NEGOTIATIONS_PANEL}
        slot="tabpanel"
      ></zero-tab-panel>
      <zero-tab-panel
        id=${BUYER_DASHBOARD_GRID_TABS.EXECUTED_PANEL}
        slot="tabpanel"
      ></zero-tab-panel>
      ${when(
        (x) => !x.showOnlyNegotiations(),
        html`
          <zero-tab-panel
            id=${BUYER_DASHBOARD_GRID_TABS.TERMINATED_PANEL}
            slot="tabpanel"
          ></zero-tab-panel>
        `,
      )}

      <tab-buttons slot="end">
        <zero-button
          class="buttons-padding"
          slot="buttons"
          appearance="tab-primary"
          @click=${(x) => x.downloadGridCsv()}
        >
          Download
        </zero-button>

        ${when(
          (x) => x.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.UPSERT_RFQ),
          html<MyPortfolio>`
            <zero-button
              class="buttons-padding"
              slot="buttons"
              appearance="tab-primary"
              @click=${(x) => x.openRfqModal()}
            >
              ${plusIcon('start')} New RFQ
            </zero-button>
          `,
        )}
      </tab-buttons>
    </zero-tabs>

    <all-rfq
      class="rfq-grid"
      style="display: ${(x) => (x.isNegotiationTabSelected ? 'none' : '')}"
      :activeTabId=${(x) => x.rfqGridTabs.activeid}
      ${ref('allRfq')}
    ></all-rfq>

    <negotiation-seller-dashboard
      class="vppa-grid"
      ${ref('negotiationGrid')}
      style="display: ${(x) => (x.isNegotiationTabSelected ? '' : 'none')}"
      :activeTabId=${(x) => x.rfqGridTabs.activeid}
    ></negotiation-seller-dashboard>
  </div>
`;
